import React, { useEffect, useState } from 'react';
import { X, Eye, CheckCircle2, TrendingUp, Loader2, ShieldAlert } from 'lucide-react';

const StatTile = ({ icon: Icon, label, value }) => (
  <div className="flex-1 border border-[#D8DDE6] rounded-[4px] p-3">
    <div className="flex items-center gap-1.5 text-[#706E6B] mb-1">
      <Icon size={13} />
      <span className="text-[10px] font-bold">{label}</span>
    </div>
    <p className="text-[20px] font-bold text-[#181818]">{value}</p>
  </div>
);

/**
 * Read-only report (Phase 9) — pulled fresh each time the panel opens
 * rather than kept in FormBuilderPage state, since it reflects live
 * visitor activity that changes independently of the schema being
 * edited. The funnel bars use `views` as their scale so a page with the
 * same reach as the initial view (i.e. no drop-off yet) reads as a full
 * bar, and later pages shrink relative to it.
 */
export default function AnalyticsPanel({ formId, onClose, fetchAnalytics }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchAnalytics()
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || 'Failed to load analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const maxScale = data?.views || 1;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-[480px] max-h-[100dvh] overflow-y-auto bg-white rounded-t-lg sm:rounded-lg shadow-xl p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <p className="text-[16px] font-bold text-[#2E2E2E]">Analytics</p>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X size={18} /></button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-10 text-[#706E6B]">
            <Loader2 size={18} className="animate-spin mr-2" /> Loading…
          </div>
        )}

        {!loading && error && (
          <p className="text-[12px] text-[#EA001E]">{error}</p>
        )}

        {!loading && !error && data && (
          <>
            <div className="flex gap-3 mb-5">
              <StatTile icon={Eye} label="Views" value={data.views} />
              <StatTile icon={CheckCircle2} label="Submissions" value={data.submissions} />
              <StatTile icon={TrendingUp} label="Conversion" value={`${(data.conversionRate * 100).toFixed(1)}%`} />
            </div>

            {data.views === 0 ? (
              <p className="text-[12px] text-[#706E6B]">No visitors tracked yet — share the form's public link to start collecting data.</p>
            ) : (
              <div>
                <p className="text-[11px] font-bold text-[#706E6B] mb-3">
                  Page-by-Page Funnel
                </p>
                <div className="space-y-2.5">
                  {data.pageFunnel.map((page) => {
                    const pct = maxScale > 0 ? Math.round((page.reached / maxScale) * 100) : 0;
                    return (
                      <div key={page.pageIndex}>
                        <div className="flex items-center justify-between text-[12px] mb-1">
                          <span className="text-[#181818] font-medium truncate">{page.title}</span>
                          <span className="text-[#706E6B] shrink-0 ml-2">{page.reached} reached</span>
                        </div>
                        <div className="h-2 bg-[#F0F1F3] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#0176D3] rounded-full transition-all"
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {data.spamBlocked > 0 && (
              <div className="mt-4 pt-4 border-t border-[#F0F1F3] flex items-center gap-2 text-[12px] text-[#706E6B]">
                <ShieldAlert size={14} className="text-[#B75C00] shrink-0" />
                {data.spamBlocked} spam submission{data.spamBlocked === 1 ? '' : 's'} silently blocked.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
